import {
  Clock,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  Object3D,
  Mesh,
  Material,
  PCFSoftShadowMap,
  SRGBColorSpace,
} from "three";
import { CSM } from "three/examples/jsm/csm/CSM.js";

import { acceleratedRaycast } from "three-mesh-bvh";

import { CSS3DRenderer } from "three/examples/jsm/renderers/CSS3DRenderer.js";

import { fetchActorResources } from "./world/actor/actor";
import { sleep, toEpoch } from "../lib/misc/util";
import { Context, PoserMode, PoserOptions } from "./context";
import { Frames } from "./util/frames";
import { CameraController, CameraMode } from "./camera/controller";
import { load } from "opentype.js";

import {
  GameStateManager,
  timeInMsToGumboTimecode,
} from "./managers/game-state";
import { DefensivePlayMode, PlayStateManager } from "./managers/playstate";
import { PoserClock } from "./util/clock";
import { FieldVisionController } from "./interface/FieldVisionController";
import { LoadingSpinner } from "./util/spinner";
import { EventList, TrackedEventsGenerator } from "./util/events";
import { PoserMonitor } from "./util/monitor";
import { World } from "./world/world";
import { BufferManager } from "./managers/buffer";
import { DEFAULT_ASSET_BASE } from "./constants";
import { UserOptions } from "./util/user-options";
import { ActorTransition } from "./world/actor/util/transitioner";
import { PostprocessManager } from "./managers/post-process";
import { Keyboard } from "./interface/Keyboard";
import { KeyboardHoldControllerStrategy } from "./camera/strategy/keyboard/hold";
import { CameraModeOptions } from "./camera/strategy/strategy";
import { VideoBoardManager } from "./managers/video-board";

// accelerate raycast
Mesh.prototype.raycast = acceleratedRaycast;

const identity = () => {};

interface CameraChangeEvent {
  mode: CameraMode;
  options?: CameraModeOptions;
}

export interface PoserCallbacks {
  onGumboChange(gumboTimeCode: string): void;
  onCanPlay(): void;
  onCameraChange(e: CameraChangeEvent): void;
  onRenderLoopTick(): void;
  onFullscreen(enteringFullscreen: boolean): void;
  onInfoButton(): void;
}

export class PoserApp {
  private static canvas?: HTMLCanvasElement;
  private subscribers: Map<string, (context: Context) => void>;
  private pctLoadedSubscribers: Map<string, (pctLoaded: number) => void>;
  context: Context;
  keyboard?: Keyboard;

  // private prevNowNum = -1;

  // private actorsToAdd: { current: number; max: number };
  // private actorRate: number;
  // private maxFrameInterval: number;
  // private stadiumLoadedCnt = { current: 0, max: 15 };
  // private prevPlayerType?: PlayerType;

  // private positions = {
  //   toChange: new Map<number, Player | undefined>(),
  //   toRemove: new Set<number>(),
  // };

  private maxLiveDelta = 5; //s

  private windowDims: { width?: number; height?: number } = {};

  addSubscribers(name: string, cb: (context: Context) => void) {
    if (this.subscribers.has(name)) {
      console.error(`adding duplicate subscriber ${name}`);
    }
    this.subscribers.set(name, cb);
  }

  addPctLoadedSubscribers(name: string, cb: (pctLoaded: number) => void) {
    if (this.pctLoadedSubscribers.has(name)) {
      console.error(`adding duplicate subscriber ${name}`);
    }
    this.pctLoadedSubscribers.set(name, cb);
  }

  constructor(options: PoserOptions) {
    if (!PoserApp.canvas) {
      PoserApp.canvas = document?.createElement("canvas");
    }

    this.subscribers = new Map();
    this.pctLoadedSubscribers = new Map();

    window.addEventListener("visibilitychange", this.visibilityCb);
    window.addEventListener("resize", this.windowResizeCb);

    this.context = this.resetContext(options);

    // TODO: let's increase rate when device != mobile
    // this.actorsToAdd = { current: maxActorsToAdd, max: maxActorsToAdd };
    // this.actorRate = 1 / 5;

    // this.maxFrameInterval = 1 / minFps;
  }

  resetContext(options: PoserOptions): Context {
    const context = {
      scene: new Scene(),
      renderer: new WebGLRenderer(
        Object.assign(
          {
            antialias: true,
            logarithmicDepthBuffer: true,
            powerPreference: "high-performance",
            canvas: PoserApp.canvas,
          },
          options.webGLRendererParameters
        )
      ),
      camera: new PerspectiveCamera(
        45,
        window.innerWidth / window.innerHeight,
        1,
        2000
      ),

      options: options,
      userOptions: new UserOptions(
        options.userOptionsNamespace,
        options.userOptionsOverrides
      ),

      clock: new PoserClock(),
      // TODO: up this back to 1500 after debugging
      trackingFps: 30,

      debugClock: new Clock(),
      rafRequestId: -1,

      postProcess: new PostprocessManager(),
      bufferManager: new BufferManager(),
      gameState: new GameStateManager(),
      videoBoardManager: new VideoBoardManager(),

      mainFrameManager: new Frames(),
      futureFrameManager: new Frames(Infinity),

      useStaticData: false,

      futureEventList: new EventList(),

      outfitTemplates: new Map(),
      materialsTemplates: new Map(),

      loadingSpinner: new LoadingSpinner(),

      keyboardEnabled: options.keyboardEnabled,

      callbacks: {
        onGumboChange: identity,
        onCanPlay: identity,
        onCameraChange: identity,
        onRenderLoopTick: identity,
        onInfoButton: identity,
        onFullscreen: identity,
      },

      state: {
        focus: {
          inFocus: true,
          wasPlaying: false,
        },

        playMode: DefensivePlayMode.BEGIN,

        isFullscreen: false,

        model: true,
        skeleton: false,

        groundSpot: false,
        stadiumEnabled: true,
        groundPlane: true,

        pointSkel: false,
        fabrikSkel: false,

        nameplatePositions: new Set([1, 10]),

        trackingDebugEnabled: false,
        actorTrailsEnabled: false,
        ballEnabled: true,
        padEnabled: true,
        trailEnabled: true,
        ballPadOpacity: 0.5,
        ballPadColor: 0xffffff,
        ballTrailColor: 0xbd9e4d,
        ballScale: 2,

        batEnabled: false,
        showBatPoints: false,

        positionId: 1,
        everyone: true,
        centralise: false,
        modelName: "generic/generic-lod.gltf",
        groundTweak: 0,

        actorTransition: "off" as ActorTransition,
        actorSpotColor: 0x041e42,

        cameraMode: CameraMode.MAP,
        cameraAuxMode: "none",
        cameraZoom: 1.0,

        printCam: false,
        boundariesVisible: false,
        stadiumCollision: true,

        playStateLead: 2,
        actionCamTransDuration: 2,

        now: 0,
        bufferStart: 0,
        bufferEnd: 0,

        debugSeeking: false,
        debugClock: false,

        playing: false,
        playbackSpeed: 1,

        stepping: false,

        playSelection: "",

        targetFps: 240,
        measureFps: false,

        recordingData: false,
        exportRecording: false,
        emulatePlayEvents: false,
        warnOnRemovePlayEvents: true,
        showBallOwner: false,
        printState: false,

        resetBenchmark: false,

        stressMultiple: 1,

        lightHelpersOn: false,

        lightTimeDebug: false,
        lightTimeOffset: 0,
        lightTimeUTC: "n/a",
        lightTimeLocal: "n/a",

        hemiIntensity: 0.5,
        hemiSkyColor: 0x2c56c0,
        hemiGroundColor: 0x253e25,

        sunX: 0,
        sunY: 0,
        sunZ: 0,
        sunIntensity: 4,
        bias: -0.0015,
        lightMargin: 500,
        lightNear: 0,
        lightFar: 2000,

        crowdEnabled: true,
        tifoEnabled: options.crowdTifo === true,
        waveEnabled: true,
        crowdEnergy: 0.5,
        crowdSpeed: 0.5,

        resetUserOptions: false,
        applyUserOptions: false,
        presetUserOptions: "-",
        printUserOptions: false,
      },
    };
    context.renderer.localClippingEnabled = true;

    context.callbacks = Object.assign(context.callbacks, options.callbacks);

    return context;
  }

  async init(container: HTMLElement): Promise<FieldVisionController> {
    // NB: below used to avoid GPU memory pipeline crash on iOS
    //     (https://discourse.threejs.org/t/textures-in-gltf-sometimes-display-black-but-only-on-ios/30520/26)
    // @ts-ignore
    window.createImageBitmap = undefined;

    const {
      bufferManager: buffer,
      camera,
      clock,
      debugClock,
      futureFrameManager,
      mainFrameManager,
      options,
      postProcess,
      state,
      videoBoardManager,
    } = this.context;

    // Default tracking data to `npd` if not overridden by poser's consumer
    options.trackingApiDomain =
      options.trackingApiDomain ?? "https://fieldvision-hls.mlbinfra.com";
    options.trackingApiBasePath = options.trackingApiBasePath ?? "mannequin";

    if (!options.assetBase) {
      options.assetBase = DEFAULT_ASSET_BASE;
    }

    this.context.container = container;

    postProcess.init(this.context);
    futureFrameManager.init(buffer.frames);
    mainFrameManager.init(buffer.frames);

    const fieldVisionController = new FieldVisionController(this);

    debugClock.start();

    if (options.loop != undefined) {
      clock.loop = options.loop;
    }

    if (options.batEnabled) {
      state.batEnabled = options.batEnabled;
    }

    if (options.playId) {
      if (options.playbackMode != undefined) {
        console.warn(
          `overriding poser mode specified in options (${options.playbackMode})`
        );
      }

      options.playbackMode = PoserMode.PLAY;
    } else if (options.seekTo) {
      if (options.playbackMode != undefined) {
        console.warn(
          `overriding poser mode specified in options (${options.playbackMode})`
        );
      }

      options.playbackMode = PoserMode.VOD;
    } else if (options.playbackMode == undefined) {
      // default to live
      options.playbackMode = PoserMode.LIVE;
    }

    videoBoardManager.init(this.context);

    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();

    this.windowDims.height = container.clientHeight;
    this.windowDims.width = container.clientWidth;

    const auxs = [KeyboardHoldControllerStrategy];
    this.context.cameraController = new CameraController(this.context, auxs);

    this.configureGraphics(container);
    this.keyboard = new Keyboard(fieldVisionController, this.context);
    if (options.keyboardEnabled) {
      this.context.keyboardEnabled = options.keyboardEnabled;
    }

    const params = new URLSearchParams(window.location.search);
    const recording = !!params.get("recording");
    state.recordingData = recording;
    if (recording) {
      console.warn("recording frames - use only for debug");
    }

    this.context.gameState.init(this.context);

    if (!this.context.playStateManager) {
      this.context.playStateManager = new PlayStateManager(this.context);
    }

    if (!this.context.eventGenerator) {
      this.context.eventGenerator = new TrackedEventsGenerator(this.context);
    }

    const fontFile = `https://www.mlbstatic.com/mlb.com/fonts/proxima-nova-semibold.woff`;
    load(fontFile, (err, font) => {
      if (err || !font) {
        console.error(`error loading ${fontFile}:`, err);
      } else {
        this.context.playerFont = font;
      }
    });

    const altFontFile = `https://www.mlbstatic.com/mlb.com/fonts/proxima-nova-bold.woff`;
    load(altFontFile, (err, font) => {
      if (err || !font) {
        console.error(`error loading ${fontFile}:`, err);
      } else {
        this.context.playerFontAlt = font;
      }
    });

    // fetch all resources and initialise things in order
    this.initBuffer(options)
      .then(() => {
        this.loadedLevel(50);
        this.context.gameState.init(this.context);
        return fetchActorResources(this.context);
      })
      .then(() => {
        this.loadedLevel(80);
        this.context.world = new World(this.context);
        this.context.scene.add(this.context.world);
        return this.context.world.init();
      })
      .then(() => {
        this.loadedLevel(90);
        // start performance monitor
        this.context.monitor = new PoserMonitor(this.context);
      })
      .then(() => {
        [[300, 100]].forEach(([delay, pct]) => {
          setTimeout(() => {
            // report init ready to consumers
            if (pct === 100) {
              this.context.callbacks.onCanPlay();
            }
            this.loadedLevel(pct);
          }, delay);
        });

        return this.renderLoop();
      })
      .catch((error) => console.error(error));

    return fieldVisionController;
  }

  teardown(isInternalReset: boolean = false) {
    const {
      bufferManager: buffer,
      camera,
      cameraController,
      monitor,
      rafRequestId,
      renderer,
      scene,
      ui,
      world,
      videoBoardManager,
    } = this.context;

    this.keyboard?.teardown();

    cameraController?.teardown();

    world?.teardown();

    cancelAnimationFrame(rafRequestId);

    const disposeMats = (mat: Material) => {
      for (const key of Object.keys(mat)) {
        const value = mat[key];
        if (!value) continue;

        if (value.dispose && typeof value.dispose === "function") {
          value.dispose();
        }

        const mapTypes = [
          "map",
          "normalMap",
          "aoMap",
          "metalnessMap",
          "roughnessMap",
        ];
        for (const mapType of mapTypes) {
          if (
            value[mapType]?.dispose &&
            value[mapType].dispose === "function"
          ) {
            value[mapType].dispose();
          }
        }
      }

      mat.dispose();
    };
    const disposeObjects = (obj: any) => {
      if (obj.material) {
        if (obj.material instanceof Array) {
          for (const mat of obj.material) {
            disposeMats(mat);
          }
        } else {
          disposeMats(obj.material);
        }
      }
      if (obj.geometry) {
        obj.geometry?.dispose();
      }

      obj.removeFromParent();
    };

    const toCleanup: Object3D[] = [];

    scene.traverse((obj) => toCleanup.push(obj));
    camera.traverse((obj) => toCleanup.push(obj));

    while (toCleanup.length > 0) {
      disposeObjects(toCleanup.pop());
    }

    const gl = renderer.getContext();
    var numTextureUnits = gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS);
    for (var unit = 0; unit < numTextureUnits; ++unit) {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, null);
      gl.bindTexture(gl.TEXTURE_CUBE_MAP, null);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, null);
    gl.bindRenderbuffer(gl.RENDERBUFFER, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    buffer.teardown();

    renderer.dispose();
    renderer.renderLists.dispose();

    ui?.teardown();

    monitor?.teardown();

    videoBoardManager.teardown();

    window.removeEventListener("visibilitychange", this.visibilityCb);
    window.removeEventListener("resize", this.windowResizeCb);
  }

  async seekTo(realSeconds: number) {
    const { bufferManager: buffer, clock } = this.context;
    console.log(`seeking to ${realSeconds}`);

    this.context.clock.real = realSeconds;

    // Refresh play state
    this.context.playStateManager = new PlayStateManager(this.context);

    while (clock.atBufferLimit()) {
      buffer.update(clock);
      await sleep(100);
    }
  }

  private loadedLevel(level: number) {
    this.pctLoadedSubscribers.forEach((cb) => cb(level));
  }

  // callbacks
  private visibilityCb = () => {
    const { bufferManager: buffer, clock, options, state } = this.context;
    if (document.visibilityState === "hidden") {
      state.focus.inFocus = false;
      state.focus.wasPlaying = state.playing;

      state.playing = false;
    } else {
      state.focus.inFocus = true;

      if (options.playbackMode == PoserMode.LIVE) {
        const seekBackToLive = async () => {
          const limits = buffer.getLimits();
          if (limits != undefined) {
            // compare live to current clock and see if we want to seek there
            const delta = limits.live - clock.real;
            console.log(
              `delta: ${delta}s / ${delta / 60}mins / ${delta / 60 / 60}hrs`
            );

            if (delta > this.maxLiveDelta) {
              this.seekTo(limits.live);
            }
          } else {
            console.warn("issue determining live time of game");
          }

          state.playing = state.focus.wasPlaying;
        };

        seekBackToLive();
      } else {
        state.playing = state.focus.wasPlaying;
      }
    }
  };

  // this.pointer.x =
  //     (event.offsetX / controller.contextRef.container.clientWidth) * 2 - 1;
  //   this.pointer.y =
  //     -(event.offsetY / controller.contextRef.container.clientHeight) * 2 + 1;

  private windowResizeCb = () => {
    if (!this.context.container) return;

    const width = this.context.container.clientWidth;
    const height = this.context.container.clientHeight;

    if (width == this.windowDims.width && height == this.windowDims.height) {
      return;
    }

    const { camera, postProcess, renderer, world } = this.context;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    const sun = world?.lights.sky?.sun;
    if (sun instanceof CSM) {
      sun.update();
    }

    if (
      this.windowDims.width == undefined ||
      this.windowDims.height == undefined ||
      (this.windowDims.width <= width && this.windowDims.height <= height)
    ) {
      // on fullscreen transition, defer setSize call to fix ios crash
      setTimeout(() => {
        renderer.setSize(width, height);
        postProcess.resize(width, height);
      }, 100);
    } else {
      renderer.setSize(width, height);
      postProcess.resize(width, height);
    }

    this.windowDims.width = width;
    this.windowDims.height = height;
  };

  // main loop
  private renderLoop() {
    const {
      bufferManager: buffer,
      clock,
      gameState,
      monitor,
      state,
    } = this.context;

    if (state.targetFps == 0) {
      console.error("invalid fps");
    }

    const fpsInterval = 1 / state.targetFps;

    monitor?.update(clock);

    clock.tick(state.playing || state.stepping);
    buffer.update(clock);

    if (state.debugClock) {
      console.log(
        `clock relative: ${clock.relative} absolute: ${
          clock.real
        } utc: ${timeInMsToGumboTimecode(clock.realMs)}`
      );
    }

    clock.utcOffsetHr = gameState.getVenueUTCOffset();

    // enforce target fps
    if (clock.elapsedTime > fpsInterval) {
      const frameNum = this.animateScene();

      // reset frame time monitor
      clock.resetReference(clock.elapsedTime % fpsInterval);

      if (state.stepping) {
        if (frameNum !== undefined) {
          if (state.stepFrame === undefined) {
            state.stepFrame = frameNum;
          } else if (state.stepFrame !== frameNum) {
            state.stepping = false;
            state.stepFrame = undefined;
          }
        } else {
          console.warn("undefined frameNum");
        }
      }
    }

    for (const cb of this.subscribers.values()) {
      cb(this.context);
    }

    this.context.callbacks.onRenderLoopTick();

    this.context.rafRequestId = requestAnimationFrame(() => this.renderLoop());
  }

  private animateScene(): number | undefined {
    const { camera, cameraController, clock, postProcess, scene, world } =
      this.context;

    const frameNum = this.updateTrackingDependencies();

    cameraController?.update(clock.delta, clock.gameDelta, frameNum);

    const sun = world?.lights.sky?.sun;
    if (sun instanceof CSM) {
      sun.update();
    }

    postProcess.render();

    return frameNum;
  }

  private updateTrackingDependencies(): number | undefined {
    const {
      bufferManager,
      clock,
      container,
      eventGenerator,
      futureEventList,
      futureFrameManager,
      gameState,
      loadingSpinner,
      mainFrameManager,
      playStateManager,
      postProcess,
      state,
      world,
    } = this.context;

    if (bufferManager.frames.getBufferLength() <= 0 || !world) {
      return undefined;
    }

    let frameNum: number | undefined = undefined;

    loadingSpinner?.update(clock, container);

    state.now = clock.relative;
    state.bufferStart = Math.max(
      clock.bufferLimits[0] - clock.timelineLimits[0],
      0
    );
    state.bufferEnd = clock.bufferLimits[1] - clock.timelineLimits[0];

    // TODONOW: reinstate
    // get and process frames in "future"
    futureFrameManager.update();

    // TODO: move futureFrameManager stuff to TrackingDataManager?
    // Emulate play events
    if (state.emulatePlayEvents) {
      eventGenerator?.writeEvents(futureFrameManager);
    }

    futureEventList.update(futureFrameManager);

    if (!clock.atBufferLimit()) {
      // get and process "now" frame
      mainFrameManager.update(clock.real);

      // console.log("mainFrameManager.missed", mainFrameManager.missed.length);

      // game state machine
      gameState.update(mainFrameManager);

      postProcess.update();

      // play state machine (tracking data related)
      playStateManager?.update(mainFrameManager, futureEventList);
    }

    world.update(clock, gameState, mainFrameManager);

    return mainFrameManager.current?.num;
  }

  private async initBuffer(options: PoserOptions) {
    await this.context.bufferManager.init(this.context);
    const limits = this.context.bufferManager.getLimits();

    this.loadedLevel(10);

    if (
      options.playbackMode == PoserMode.PLAY &&
      this.context.bufferManager.plays &&
      options.playId
    ) {
      const requestedPlay = this.context.bufferManager.plays[options.playId];
      this.context.clock.playLimits = [
        requestedPlay.startTime,
        requestedPlay.endTime,
      ];
    }

    if (limits == undefined) {
      console.error("no live value returned from data manager");
    } else {
      if (options.playbackMode == PoserMode.LIVE) {
        console.log("seeking to live");
        this.seekTo(limits.live);
      } else if (
        options.playbackMode == PoserMode.PLAY &&
        this.context.clock.playLimits
      ) {
        console.log(
          "seeking to start of requested playId: ",
          options.playId,
          this.context.clock.playLimits[0]
        );
        return this.seekTo(this.context.clock.playLimits[0]);
      } else if (options.playbackMode == PoserMode.VOD) {
        if (options.seekTo) {
          return this.seekTo(toEpoch(options.seekTo) / 1000);
        } else {
          console.warn("no seekTo specified, seeking to start");
          return this.seekTo(limits.start);
        }
      }
    }
  }

  private configureGraphics(container: HTMLElement) {
    const { camera, renderer, userOptions } = this.context;
    const width = container.clientWidth;
    const height = container.clientHeight;

    renderer.setPixelRatio(window.devicePixelRatio);
    camera.aspect = width / height;
    renderer.setSize(width, height);
    renderer.outputColorSpace = SRGBColorSpace;

    if (userOptions.hasShadows()) {
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = userOptions.getShadowMapType();
    }

    container.appendChild(renderer.domElement);

    const betaContainer = document.createElement("div");
    betaContainer.setAttribute(
      "style",
      "position: absolute; font-size: 14px; top: 5px; width: 100%; text-align: center; display: block; user-select: none; pointer-events: none;"
    );
    container.appendChild(betaContainer);
  }
}
